'use client';

import React, { useState, useEffect } from 'react';
import WelcomePhase from './WelcomePhase';
import SubmissionForm from './SubmissionForm';
import ImageRevealPhase from './ImageRevealPhase';
import ManateeGamePhase from './ManateeGamePhase';
import SuccessPhase from './SuccessPhase';
import ConfettiOverlay from './ConfettiOverlay';
import SalesRepEntryModal from './SalesRepEntryModal';
import { entriesService } from '@/lib/services/entriesService';
import { gameSettingsService } from '@/lib/services/gameSettingsService';
import Image from 'next/image';

export type GamePhase = 'welcome' | 'form' | 'game-select' | 'image' | 'manatee' | 'offer-manatee' | 'success';

export interface EntryData {
  name: string;
  email: string;
  phone: string;
  company: string;
  guess: number;
}

export default function GameFlow() {
  const [phase, setPhase] = useState<GamePhase>('welcome');
  const [showConfetti, setShowConfetti] = useState(false);
  const [submittedEntry, setSubmittedEntry] = useState<EntryData | null>(null);
  const [currentEntryId, setCurrentEntryId] = useState<string | null>(null);
  const [currentRaffleCode, setCurrentRaffleCode] = useState<string | null>(null);
  const [gamesPlayed, setGamesPlayed] = useState<string[]>([]);
  const [lastGuess, setLastGuess] = useState<number>(0);
  const [lastManateeTimeMs, setLastManateeTimeMs] = useState<number | null>(null);
  const [gameEnabled, setGameEnabled] = useState<boolean | null>(null); // null = loading
  const [showSalesRepEntry, setShowSalesRepEntry] = useState(false);

  useEffect(() => {
    // Load initial game enabled state
    gameSettingsService.getSettings().then((s) => setGameEnabled(s.gameEnabled));

    // Subscribe to real-time changes
    const unsubscribe = gameSettingsService.subscribeToSettings((s) => {
      setGameEnabled(s.gameEnabled);
    });

    return () => unsubscribe();
  }, []);

  const handlePlayGame = () => {
    setPhase('form');
  };

  const handleFormSubmit = (
    entry: { name: string; email: string; phone: string; company: string },
    entryId: string,
    raffleCode?: string
  ) => {
    setSubmittedEntry({ ...entry, guess: 0 });
    setCurrentEntryId(entryId);
    setCurrentRaffleCode(raffleCode ?? null);
    setPhase('game-select');
  };

  const handleSelectGame = (game: 'image' | 'manatee') => {
    setPhase(game);
  };

  // Called when user submits their guess after the image countdown
  const handleGuessSubmit = async (guess: number) => {
    if (currentEntryId) {
      try {
        await entriesService.updateGuess(currentEntryId, guess);
      } catch {}
    }
    const updated = [...gamesPlayed, 'candy-count'];
    setGamesPlayed(updated);
    if (currentEntryId) {
      try {
        await entriesService.updateGamesPlayed(currentEntryId, updated);
      } catch {}
    }
    if (submittedEntry) {
      setSubmittedEntry({ ...submittedEntry, guess });
    }
    setLastGuess(guess);
    // After candy count, offer Find the Manatee
    setPhase('offer-manatee');
  };

  const handleManateeComplete = async (timeMs: number | null, correct: boolean) => {
    const updated = [...gamesPlayed, 'find-manatee'];
    setGamesPlayed(updated);
    setLastManateeTimeMs(timeMs);
    if (currentEntryId) {
      try {
        await entriesService.updateManateeResult(currentEntryId, updated, timeMs, correct);
      } catch {}
    }
    setPhase('success');
    setShowConfetti(correct);
    if (correct) setTimeout(() => setShowConfetti(false), 4000);
  };

  const handleSkipManatee = () => {
    setPhase('success');
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 4000);
  };

  const handlePlayAgain = () => {
    setSubmittedEntry(null);
    setCurrentEntryId(null);
    setCurrentRaffleCode(null);
    setGamesPlayed([]);
    setLastGuess(0);
    setLastManateeTimeMs(null);
    setPhase('welcome');
  };

  // Loading state while checking game status
  if (gameEnabled === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-yellow-50 to-green-100 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-green-300 border-t-green-600 rounded-full animate-spin" />
      </div>
    );
  }

  // Game disabled — show "Games Starting Soon" screen
  if (!gameEnabled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-yellow-50 to-green-100 flex flex-col items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-72 h-72 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 pointer-events-none" />

        <div className="relative z-10 w-full max-w-md text-center">
          {/* Logo */}
          <div className="flex items-center justify-center mb-8">
            <Image
              src="/assets/images/3tlogo-removebg-preview-1775658033774.png"
              alt="3T Logo"
              width={120}
              height={80}
              className="object-contain"
              priority
            />
          </div>

          <div className="bg-white rounded-3xl shadow-xl border border-green-100 p-10">
            {/* Animated icon */}
            <div className="flex items-center justify-center mb-6">
              <div className="relative">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-5xl">🦭</span>
                </div>
                <div className="absolute -top-1 -right-1 w-7 h-7 bg-yellow-400 rounded-full flex items-center justify-center shadow-md">
                  <span className="text-sm">⏳</span>
                </div>
              </div>
            </div>

            <h1 className="text-3xl font-extrabold text-gray-800 mb-3">
              Games Starting Soon
            </h1>
            <p className="text-gray-500 text-base leading-relaxed mb-6">
              The event games will be opening shortly. Please check back in a moment — we&apos;re getting everything ready for you!
            </p>

            {/* Animated dots */}
            <div className="flex items-center justify-center gap-2">
              <span className="w-2.5 h-2.5 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2.5 h-2.5 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2.5 h-2.5 bg-green-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        </div>

        <a
          href="/admin-login"
          className="absolute bottom-4 right-4 text-xs text-green-700/50 hover:text-green-700 transition-colors duration-150 font-medium"
        >
          Admin
        </a>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-yellow-50 to-green-100 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-72 h-72 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 pointer-events-none" />
      <div className="absolute top-1/2 left-1/4 w-48 h-48 bg-green-300 rounded-full mix-blend-multiply filter blur-2xl opacity-20 pointer-events-none" />

      {showConfetti && <ConfettiOverlay />}

      {showSalesRepEntry && (
        <SalesRepEntryModal onClose={() => setShowSalesRepEntry(false)} />
      )}

      <div className="relative z-10 w-full max-w-lg">
        {phase === 'welcome' && <WelcomePhase onPlay={handlePlayGame} />}
        {phase === 'form' && <SubmissionForm onSubmit={handleFormSubmit} />}
        {phase === 'game-select' && (
          <GameSelectPhase onSelect={handleSelectGame} />
        )}
        {phase === 'image' && (
          <ImageRevealPhase
            onGuessSubmit={handleGuessSubmit}
            entryId={currentEntryId}
          />
        )}
        {phase === 'manatee' && (
          <ManateeGamePhase
            onComplete={handleManateeComplete}
            entryId={currentEntryId}
          />
        )}
        {phase === 'offer-manatee' && (
          <OfferManateePhase
            guess={lastGuess}
            onPlayManatee={() => setPhase('manatee')}
            onSkip={handleSkipManatee}
          />
        )}
        {phase === 'success' && submittedEntry && (
          <SuccessPhase
            entry={submittedEntry}
            onPlayAgain={handlePlayAgain}
            gamesPlayed={gamesPlayed}
            entryId={currentEntryId}
            manateeTimeMs={lastManateeTimeMs}
            raffleCode={currentRaffleCode}
          />
        )}
      </div>

      {/* Sales Rep button */}
      <button
        onClick={() => setShowSalesRepEntry(true)}
        className="absolute bottom-4 left-4 text-xs text-indigo-600/70 hover:text-indigo-700 transition-colors duration-150 font-semibold bg-white/60 hover:bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full border border-indigo-200/60 shadow-sm"
      >
        Sales Rep
      </button>

      <a
        href="/admin-login"
        className="absolute bottom-4 right-4 text-xs text-green-700/50 hover:text-green-700 transition-colors duration-150 font-medium"
      >
        Admin
      </a>
    </div>
  );
}

// ── Game Select Phase ──────────────────────────────────────────────────────────

interface GameSelectPhaseProps {
  onSelect: (game: 'image' | 'manatee') => void;
}

function GameSelectPhase({ onSelect }: GameSelectPhaseProps) {
  return (
    <div className="animate-fade-in">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-extrabold text-gray-800">Choose Your Game</h2>
        <p className="text-gray-500 text-sm mt-1">Pick a game to play and complete your entry</p>
      </div>
      <div className="space-y-4">
        <button
          onClick={() => onSelect('image')}
          className="w-full bg-white rounded-2xl border-2 border-green-200 hover:border-green-400 shadow-md hover:shadow-lg transition-all duration-150 p-6 text-left group"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-yellow-100 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:bg-yellow-200 transition-colors">
              <span className="text-2xl">🦭</span>
            </div>
            <div>
              <h3 className="font-bold text-gray-800 text-lg">Count the Manatees</h3>
              <p className="text-gray-500 text-sm mt-0.5">Study the image for 15 seconds, then guess the number of manatees</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => onSelect('manatee')}
          className="w-full bg-white rounded-2xl border-2 border-blue-200 hover:border-blue-400 shadow-md hover:shadow-lg transition-all duration-150 p-6 text-left group"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
              <span className="text-2xl">🔍</span>
            </div>
            <div>
              <h3 className="font-bold text-gray-800 text-lg">Find the Manatee</h3>
              <p className="text-gray-500 text-sm mt-0.5">Find the manatee wearing the 3T shirt as fast as you can!</p>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}

// ── Offer Manatee Phase (shown after Candy Count) ─────────────────────────────

interface OfferManateePhaseProps {
  guess: number;
  onPlayManatee: () => void;
  onSkip: () => void;
}

function OfferManateePhase({ guess, onPlayManatee, onSkip }: OfferManateePhaseProps) {
  return (
    <div className="animate-fade-in">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500 rounded-full shadow-xl shadow-green-200 mb-4">
          <span className="text-3xl">🎯</span>
        </div>
        <h2 className="text-2xl font-extrabold text-gray-800">Guess Submitted!</h2>
        <p className="text-gray-500 text-sm mt-1">
          Your guess of <span className="font-bold text-green-600">{guess.toLocaleString()}</span> has been recorded.
        </p>
      </div>

      <div className="bg-white rounded-3xl shadow-xl border border-green-100 p-6 mb-4">
        <div className="text-center mb-5">
          <div className="inline-flex items-center gap-2 bg-blue-50 rounded-full px-4 py-2 border border-blue-100 mb-3">
            <span className="text-lg">🦭</span>
            <span className="text-blue-700 font-semibold text-sm">Want to play another game?</span>
          </div>
          <p className="text-gray-500 text-sm">
            Try <strong>Find the Manatee</strong> — spot the manatee in the 3T shirt as fast as you can!
          </p>
        </div>

        <button
          onClick={onPlayManatee}
          className="w-full bg-blue-600 hover:bg-blue-700 active:scale-95 text-white font-bold text-base py-4 rounded-2xl shadow-lg shadow-blue-200 transition-all duration-150 flex items-center justify-center gap-2 mb-3"
        >
          <span className="text-xl">🦭</span>
          Play Find the Manatee
        </button>

        <button
          onClick={onSkip}
          className="w-full bg-white hover:bg-gray-50 active:scale-95 text-gray-500 font-semibold text-sm py-3 rounded-2xl border-2 border-gray-200 transition-all duration-150"
        >
          No thanks, I&apos;m done
        </button>
      </div>
    </div>
  );
}